import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTypedSelector } from "../../store";
import { 
  FaHome,
  FaStore,
  FaShoppingBag,
  FaMotorcycle,
  FaUtensils,
  FaMapMarkerAlt,
  FaUserShield,
  FaClipboardList,
  FaAd,
  FaChevronDown,
  FaChevronRight
} from "react-icons/fa";
import classNames from "classnames";

interface MenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  children?: { label: string; path: string }[];
}

const NavMenu = () => {
  const { admin } = useTypedSelector((state) => state.session);
  const location = useLocation();
  
  // Track expanded menus
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  
  // Helper to check if any child is active to keep parent expanded/highlighted
  const isChildActive = (children: { path: string }[]) => 
    children.some(child => location.pathname === child.path);

  const basicPages: MenuItem[] = [
    { 
        label: "Tableau de bord", 
        path: "/", 
        icon: <FaHome /> 
    },
    { 
        label: "Villes", 
        path: "/cities", 
        icon: <FaMapMarkerAlt /> 
    },
    { 
        label: "Commandes", 
        path: "/orders", 
        icon: <FaShoppingBag /> 
    },
    {
      label: "Restaurants",
      path: "/restaurants", // Parent path
      icon: <FaStore />,
      children: [
        { label: "Tous les restaurants", path: "/restaurants" },
        { label: "Ajouter un restaurant", path: "/restaurants/create" },
      ],
    },
    {
      label: "Livreurs",
      path: "/riders", // Parent path
      icon: <FaMotorcycle />,
      children: [
        { label: "Tous les livreurs", path: "/riders" },
        { label: "Ajouter un livreur", path: "/riders/create" },
      ],
    },
    {
      label: "Produits",
      path: "/items", // Parent path logic
      icon: <FaUtensils />,
      children: [
        { label: "Toutes les catégories", path: "/headers" },
        { label: "Ajouter une catégorie", path: "/headers/create" },
        { label: "Tous les produits", path: "/items" },
        { label: "Ajouter un produit", path: "/items/create" },
        { label: "Tous les groupes", path: "/modifier-groups" },
        { label: "Ajouter un groupe", path: "/modifier-groups/create" },
        { label: "Tous les suppléments", path: "/modifier-options" },
        { label: "Ajouter un supplément", path: "/modifier-options/create" },
      ],
    },
    {
      label: "Candidatures",
      path: "/applications",
      icon: <FaClipboardList />,
      children: [
        { label: "Restaurants", path: "/applications/restaurants" },
        { label: "Livreurs", path: "/applications/riders" },
      ],
    },
    {
      label: "Bannières",
      path: "/ads",
      icon: <FaAd />,
      children: [
        { label: "Toutes les bannières", path: "/ads" },
        { label: "Ajouter une bannière", path: "/ads/create" },
      ],
    },
  ];

  const menuItems = [...basicPages];
  if (admin?.isSuperAdmin) {
    menuItems.push({
      label: "Admins",
      path: "/admins",
      icon: <FaUserShield />,
      children: [
        { label: "Tous les admins", path: "/admins" },
        { label: "Ajouter un admin", path: "/admins/create" },
      ],
    });
  }

  return (
    <aside className="w-64 bg-white border-r border-gray-200 flex flex-col h-full flex-shrink-0 sticky top-0 left-0 z-30">
      {/* Brand / Logo Area */}
      <div className="h-16 flex items-center justify-center border-b border-gray-100 bg-primary bg-opacity-5">
        <h1 className="text-xl font-bold text-primary tracking-wide uppercase">Admin Panel</h1>
      </div>
      
      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 scrollbar-thin scrollbar-thumb-gray-200">
        {menuItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const parentActive = hasChildren ? isChildActive(item.children!) : isActive(item.path);
            
            // Auto-expand if a child is active
            const isExpanded = expandedMenus.includes(item.label) || (hasChildren && isChildActive(item.children!));

            if (!hasChildren) {
                return (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={classNames(
                            "group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200",
                            isActive(item.path)
                                ? "bg-primary text-white shadow-sm"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                    >
                        <span className={classNames(
                            "mr-3 flex-shrink-0 text-lg",
                            isActive(item.path) ? "text-white" : "text-gray-400 group-hover:text-gray-500"
                        )}>
                            {item.icon}
                        </span>
                        {item.label}
                    </Link>
                );
            }

            // Render Parent with Children
            return (
                <div key={item.label} className="space-y-1">
                    <button
                        onClick={() => toggleMenu(item.label)}
                        className={classNames(
                            "w-full group flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 focus:outline-none",
                            parentActive 
                                ? "bg-blue-50 text-primary" 
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                    >
                        <div className="flex items-center">
                            <span className={classNames(
                                "mr-3 flex-shrink-0 text-lg",
                                parentActive ? "text-primary" : "text-gray-400 group-hover:text-gray-500"
                            )}>
                                {item.icon}
                            </span>
                            {item.label}
                        </div>
                        {isExpanded ? (
                            <FaChevronDown className="w-3 h-3 text-gray-400" />
                        ) : (
                            <FaChevronRight className="w-3 h-3 text-gray-400" />
                        )}
                    </button>

                    {/* Submenu */}
                    {isExpanded && (
                        <div className="space-y-1 ml-9 border-l border-gray-100 pl-1">
                            {item.children!.map((child) => (
                                <Link
                                    key={child.path}
                                    to={child.path}
                                    className={classNames(
                                        "group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                                        isActive(child.path)
                                            ? "text-primary bg-blue-50/50"
                                            : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                                    )}
                                >
                                    {child.label}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            );
        })}
      </nav>

      {/* Footer / User Info could go here */}
      <div className="p-4 border-t border-gray-100 text-xs text-center text-gray-400">
        &copy; {new Date().getFullYear()} Dashboard
      </div>
    </aside>
  );
};

export default NavMenu;
